/**
 * Light Theme - Enterprise SaaS
 *
 * Neutral work surfaces with blue primary actions, teal analytical accents,
 * and amber warning/CTA affordances.
 */
const lightTheme = {
  name: 'light',
  label: 'Light',
  mode: 'light',
  colorSet: 'default',

  colors: {
    // Background elevation system — wider steps so white cards visibly float above canvas
    canvas: '#DDE4EE',
    panelPrimary: '#FFFFFF',
    panelSecondary: '#F2F5F9',
    panelTertiary: '#E7EDF5',
    panelQuaternary: '#DCE3ED',
    surfaceHover: '#E2E9F2',

    // Borders — dark enough to read against white without feeling heavy
    borderSubtle: '#B8C3D2',
    borderStrong: '#8594A7',

    // Text hierarchy
    textHeading: '#0C1220',
    textPrimary: '#1A2233',
    textSecondary: '#2E3D52',
    textSupporting: '#445162',
    textNeutral: '#566475',
    textMuted: '#596573',
    textInverse: '#FFFFFF',

    // Primary accent: enterprise blue
    accentPrimary: '#1D4ED8',
    accentPrimaryVibrant: '#1E40AF',

    // Secondary accent: analytical teal
    accentSecondary: '#0F766E',
    accentSecondaryVibrant: '#115E59',

    // Warm accent: CTA and warning amber
    accentWarm: '#B45309',
    accentWarmVibrant: '#92400E',

    // Status colors
    statusDanger: '#B91C1C',
    statusWarning: '#B45309',
    statusSuccess: '#047857',
    statusInfo: '#1D4ED8',

    // Support colors
    gridline: 'rgba(15, 23, 42, 0.08)',
    focusOutline: 'rgba(29, 78, 216, 0.42)',
    overlay: 'rgba(15, 23, 42, 0.52)',

    // Component-specific
    headerBg: '#FFFFFF',
    sidebarBg: '#CDD7E5',
    cardBg: '#FFFFFF',
    inputBg: '#FFFFFF',
    dropdownBg: '#FFFFFF',

    // Navigation sidebar
    navSectionHeader: '#4C5E72',
    navItemText: '#253548',
    navItemHover: '#0F172A',
    navItemHoverBg: 'rgba(29, 78, 216, 0.07)',
    navItemActive: '#1D4ED8',
    navItemActiveBg: 'rgba(29, 78, 216, 0.11)',
    navIconDefault: '#505C6F',
    navIconHover: '#1D4ED8',
    navIconActive: '#1D4ED8',
    navDivider: 'rgba(100, 116, 139, 0.28)',

    // Table cell colors
    tableEntity: '#123B66',
    tableMetric: '#0F6B68',
    tableBody: '#334155',
    tableSecondary: '#4B5563',
    tableTime: '#5F6B7A',

    // Quality distribution colors
    qualityP20: '#B91C1C',
    qualityP40: '#C2410C',
    qualityP60: '#A16207',
    qualityP80: '#15803D',
    qualityP100: '#047857',

    // Pill styling
    pillBgFrom: '#FFFFFF',
    pillBgTo: '#F1F5F9',
    pillActiveBgFrom: '#E8F0FE',
    pillActiveBgTo: '#DDE8FC',
    pillHoverBgFrom: '#F8FAFC',
    pillHoverBgTo: '#E9EEF5',

    // Table-specific
    tableHeaderBg: '#E5EBF4',
    tableRowHover: 'rgba(29, 78, 216, 0.07)',
    tableRowExpanded: 'rgba(15, 118, 110, 0.06)',
    buttonTextOnAccent: '#FFFFFF',

    // RGB channels for opacity modifiers
    statusDangerRgb: '185, 28, 28',
    statusSuccessRgb: '4, 120, 87',
    statusWarningRgb: '180, 83, 9',
    statusInfoRgb: '29, 78, 216',
    accentWarmRgb: '180, 83, 9',

    // Severity
    severityCriticalText: '#B91C1C',
    severityCriticalBg: 'rgba(185, 28, 28, 0.10)',
    severityCriticalBorder: 'rgba(185, 28, 28, 0.24)',
    severityCriticalGlow: '0 0 18px rgba(185, 28, 28, 0.14)',
    severityHighText: '#B45309',
    severityHighBg: 'rgba(180, 83, 9, 0.10)',
    severityHighBorder: 'rgba(180, 83, 9, 0.24)',
    severityHighGlow: '0 0 18px rgba(180, 83, 9, 0.14)',
    severityMediumText: '#A16207',
    severityMediumBg: 'rgba(161, 98, 7, 0.10)',
    severityMediumBorder: 'rgba(161, 98, 7, 0.24)',
    severityMediumGlow: '0 0 18px rgba(161, 98, 7, 0.14)',
    severityLowText: '#1D4ED8',
    severityLowBg: 'rgba(29, 78, 216, 0.10)',
    severityLowBorder: 'rgba(29, 78, 216, 0.22)',
    severityLowGlow: '0 0 18px rgba(29, 78, 216, 0.14)',
    severitySuccessText: '#047857',
    severitySuccessBg: 'rgba(4, 120, 87, 0.10)',
    severitySuccessBorder: 'rgba(4, 120, 87, 0.22)',
    severitySuccessGlow: '0 0 8px rgba(4, 120, 87, 0.14)',

    // Border hierarchy
    borderMuted: '#C8D3E0',
    borderMedium: '#A8B5C4',
    borderAccent: '#728295',

    // Surface overlays
    surfaceSubtle: 'rgba(15, 23, 42, 0.035)',
    surfaceMid: 'rgba(15, 23, 42, 0.065)',
    surfaceOverlayHover: 'rgba(29, 78, 216, 0.055)',

    // Scrollbar
    scrollbarTrack: 'rgba(203, 213, 225, 0.55)',
    scrollbarThumb: 'rgba(100, 116, 139, 0.55)',
    scrollbarThumbHover: 'rgba(71, 85, 105, 0.72)',

    // Selection
    selectionBg: 'rgba(29, 78, 216, 0.18)',

    // Graph visualization
    graphBg: '#DDE4EE',
    graphNodeBg: '#FFFFFF',
    graphNodeText: '#1A2233',
  },

  shadows: {
    card: '0 1px 3px rgba(15, 23, 42, 0.10), 0 8px 20px rgba(15, 23, 42, 0.12)',
    soft: '0 2px 8px rgba(15, 23, 42, 0.10), 0 16px 32px rgba(15, 23, 42, 0.13)',
    dropdown: '0 8px 24px rgba(15, 23, 42, 0.18), 0 1px 3px rgba(15, 23, 42, 0.12)',
    drawer: '-8px 0 24px rgba(15, 23, 42, 0.14), -1px 0 3px rgba(15, 23, 42, 0.10)',
  },
};

export default lightTheme;
